import { LS_INTERFACE_LANGUAGE } from '@/shared/constants/localStorage';

// Translation sources retain deferred locales so copy can be restored without retranslating it.
// `APP_LOCALES` is the authoritative list exposed by the runtime and language selector.
// `pt` is the existing persisted Brazilian Portuguese locale. Keep it stable for current users.
export const CATALOG_LOCALES = ['en', 'es', 'pt', 'fr', 'it', 'ru'] as const;
export type CatalogLocale = (typeof CATALOG_LOCALES)[number];

export const APP_LOCALES = ['en', 'es', 'pt', 'pt-PT', 'fr', 'ru'] as const;
export type AppLocale = (typeof APP_LOCALES)[number];

const DEFAULT_LOCALE: AppLocale = 'en';
const SUPPORTED_LOCALES = new Set<string>(APP_LOCALES);
let activeInterfaceLocale: AppLocale | undefined;

export const isAppLocale = (locale?: null | string): locale is AppLocale =>
  Boolean(locale && SUPPORTED_LOCALES.has(locale));

export const resolveInterfaceLocale = (language?: null | string): AppLocale => {
  const normalized = language?.trim().replaceAll('_', '-').toLowerCase();
  if (normalized === 'pt-pt' || normalized?.startsWith('pt-pt-')) return 'pt-PT';
  const locale = normalized?.split('-', 1)[0];
  return locale && SUPPORTED_LOCALES.has(locale) ? (locale as AppLocale) : DEFAULT_LOCALE;
};

export const getStoredInterfaceLocale = (): AppLocale | undefined => {
  try {
    const storedLocale = localStorage.getItem(LS_INTERFACE_LANGUAGE);
    return storedLocale && SUPPORTED_LOCALES.has(storedLocale) ? (storedLocale as AppLocale) : undefined;
  } catch {
    return undefined;
  }
};

export const getBrowserLanguage = () => (typeof navigator === 'undefined' ? '' : navigator.language);

export const getInitialInterfaceLocale = (): AppLocale =>
  getStoredInterfaceLocale() ?? resolveInterfaceLocale(getBrowserLanguage());

export const getActiveInterfaceLocale = (): AppLocale => {
  activeInterfaceLocale ??= getInitialInterfaceLocale();
  return activeInterfaceLocale;
};

export const setActiveInterfaceLocale = (locale: AppLocale) => {
  activeInterfaceLocale = locale;
};

export const storeInterfaceLocale = (locale: AppLocale) => {
  try {
    localStorage.setItem(LS_INTERFACE_LANGUAGE, locale);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts. The in-memory choice still works.
  }
};
